import type { FastifyRequest, FastifyReply } from 'fastify'
import { AIRepository } from './ai.repository.js'
import { tryGetBillingService } from '../billing/billing.container.js'
import { AI_QUOTA_LIMITS, resolveUserPlanTier } from './ai.quota.js'

const repository = new AIRepository()

// Re-export for other modules
export { AI_QUOTA_LIMITS, resolveUserPlanTier }
// Backward compatibility alias
export const resolveUserPlan = resolveUserPlanTier

/**
 * Middleware to check AI quota before processing
 */
export async function checkAIQuota(
  request: FastifyRequest,
  reply: FastifyReply
): Promise<void | FastifyReply> {
  const userId = request.user?.uid
  
  if (!userId) {
    const payload = {
      success: false,
      error: 'Authentication required'
    }
    // In normal Fastify usage `reply` is provided; always use it to keep return types
    // consistent. If tests call this without a `reply`, fallback to throwing.
    if (typeof reply?.code === 'function') {
      return reply.code(401).send(payload)
    }
    throw new Error('Authentication required')
  }

  try {
    const billingService = tryGetBillingService()
    if (billingService) {
      const quotaResult = await billingService.checkAndConsumeQuota(userId, 'ai_call')
      if (!quotaResult.allowed) {
        return reply.code(429).send({
          success: false,
          error: 'AI quota limit reached',
          limits: quotaResult.limits,
          usage: quotaResult.usage,
          remaining: quotaResult.remaining,
          upgradeUrl: '/subscriptions'
        })
      }

      // Billing is the source of truth; short-circuit local counters
      ;(request as any).aiUsage = {
        limits: quotaResult.limits,
        usage: quotaResult.usage,
        remaining: quotaResult.remaining,
        plan: (request.user as any)?.plan ?? 'unknown',
        source: 'billing'
      }
      return
    }

    // No billing available: allow request and skip local counters (avoid double sources of truth)
    const planTier = resolveUserPlanTier(request)
    const limits = AI_QUOTA_LIMITS[planTier]
    const usageStats = await repository.getUsageStats(userId)
    const remaining = {
      daily: Math.max(0, limits.daily - usageStats.dailyCount),
      monthly: Math.max(0, limits.monthly - usageStats.monthlyCount)
    }

    if (usageStats.dailyCount >= limits.daily || usageStats.monthlyCount >= limits.monthly) {
      return reply.code(429).send({
        success: false,
        error: 'AI quota limit reached',
        limits,
        usage: usageStats,
        remaining,
        upgradeUrl: '/subscriptions'
      })
    }

    ;(request as any).aiUsage = {
      limits,
      usage: usageStats,
      remaining,
      plan: planTier,
      source: 'fallback'
    }

    void repository.incrementUsage(userId)
    return
  } catch (error) {
    request.log.error({ error }, 'AI quota check failed')
    // Don't block request on quota check failure
  }
}

/**
 * Get user's current AI usage
 */
export async function getAIUsage(
  request: FastifyRequest,
  reply: FastifyReply
) {
  const userId = request.user?.uid
  
  if (!userId) {
    const payload = {
      success: false,
      error: 'Authentication required'
    }
    if (typeof reply?.code === 'function') {
      return reply.code(401).send(payload)
    }
    return payload
  }

  try {
    const billingService = tryGetBillingService()
    if (billingService) {
      const summary = await billingService.getSubscriptionSummary(userId)
      if (summary?.quota) {
        const planTier = (summary.plan?.tier as any) ?? resolveUserPlanTier(request)
        const limits = summary.quota.limits
        const usage = summary.quota.usage
        return reply.send({
          success: true,
          data: {
            usage,
            limits,
            remaining: {
              daily: limits.aiCallsPerDay ?? limits.aiRequestsPerDay ?? null,
              monthly: limits.aiCallsPerMonth ?? null
            },
            plan: planTier,
            source: 'billing'
          }
        })
      }
    }

    const planTier = resolveUserPlanTier(request)
    const limits = AI_QUOTA_LIMITS[planTier]
    const usageStats = await repository.getUsageStats(userId)
    const remaining = {
      daily: Math.max(0, limits.daily - usageStats.dailyCount),
      monthly: Math.max(0, limits.monthly - usageStats.monthlyCount)
    }

    return reply.send({
      success: true,
      data: {
        usage: usageStats,
        limits,
        remaining,
        plan: planTier,
        source: 'fallback'
      }
    })
  } catch (error) {
    request.log.error({ error }, 'Failed to get AI usage')
    return reply.code(500).send({
      success: false,
      error: 'Failed to fetch usage stats'
    })
  }
}
